import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../api/axiosInstance';
import { useAuth } from '../context/AuthContext';

const HEADER_H = 52;

const COLORS = [
  '#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe',
  '#43e97b', '#38f9d7', '#fa709a', '#fee140', '#a18cd1', '#fbc2eb',
  '#8fd3f4', '#84fab0', '#ff9a9e', '#ff6b6b',
];

/* ─── Toast ──────────────────────────────────────────────────────── */
function Toast({ msg, type }) {
  if (!msg) return null;
  return (
    <div style={{
      position: 'fixed', top: 72, right: 24, zIndex: 9999,
      background: type === 'error' ? 'rgba(239,68,68,0.95)' : 'rgba(16,185,129,0.95)',
      color: '#fff', padding: '0.75rem 1.25rem', borderRadius: 8,
      boxShadow: '0 4px 20px rgba(0,0,0,0.3)', fontSize: '0.9rem',
      maxWidth: 380, backdropFilter: 'blur(10px)',
    }}>
      {msg}
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────── */
export default function WatchlistManager() {
  const { user } = useAuth();

  /* watchlists state */
  const [watchlists, setWatchlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ msg: '', type: 'success' });

  /* create/edit modal */
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ watchlistName: '', description: '', color: '#667eea', watchlistId: null });
  const [saving, setSaving] = useState(false);

  /* manage-stocks modal */
  const [showStocksModal, setShowStocksModal] = useState(false);
  const [activeWL, setActiveWL] = useState(null);

  /* stock search */
  const [stockSearch, setStockSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  /* stock add form */
  const [showStockForm, setShowStockForm] = useState(null);
  const [stockFormData, setStockFormData] = useState({ symbol: '', stockName: '', entryPrice: '', targetPrice: '', stopLoss: '', notes: '' });
  const [addingSock, setAddingStock] = useState(false);

  const searchTimer = useRef(null);

  /* ── helpers ── */
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3000);
  };

  /* ── fetch all watchlists ── */
  const fetchWatchlists = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await api.get(`/watchlist/user/${user.id}`);
      setWatchlists(data.data || []);
    } catch {
      showToast('Failed to load watchlists', 'error');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchWatchlists(); }, [fetchWatchlists]);

  /* ── debounced stock search ── */
  useEffect(() => {
    if (!stockSearch.trim()) { setSearchResults([]); return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data } = await api.get(`/stocks/search?q=${encodeURIComponent(stockSearch)}&limit=20`);
        setSearchResults(data.data || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [stockSearch]);

  /* ── create/edit modal ── */
  const openCreate = () => {
    setFormData({ watchlistName: '', description: '', color: '#667eea', watchlistId: null });
    setEditMode(false);
    setShowCreateModal(true);
  };

  const openEdit = (wl, e) => {
    e.stopPropagation();
    setFormData({ watchlistName: wl.watchlistName, description: wl.description || '', color: wl.color, watchlistId: wl.watchlistId });
    setEditMode(true);
    setShowCreateModal(true);
  };

  const saveWatchlist = async () => {
    if (!formData.watchlistName.trim()) { showToast('Watchlist name is required', 'error'); return; }
    setSaving(true);
    try {
      if (editMode) {
        await api.put(`/watchlist/${formData.watchlistId}`, {
          watchlistName: formData.watchlistName,
          description: formData.description,
          color: formData.color,
        });
        showToast('Watchlist updated!');
      } else {
        await api.post('/watchlist/create', {
          userId: user.id,
          watchlistName: formData.watchlistName,
          description: formData.description,
          color: formData.color,
        });
        showToast('Watchlist created!');
      }
      setShowCreateModal(false);
      fetchWatchlists();
    } catch (err) {
      showToast(err.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteWatchlist = async (wl, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${wl.watchlistName}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/watchlist/${wl.watchlistId}`);
      showToast('Watchlist deleted');
      fetchWatchlists();
    } catch {
      showToast('Delete failed', 'error');
    }
  };

  /* ── manage stocks ── */
  const openManageStocks = (wl) => {
    setActiveWL({ ...wl, stocks: [...(wl.stocks || [])] });
    setStockSearch('');
    setSearchResults([]);
    setShowStockForm(null);
    setShowStocksModal(true);
  };

  const isStockAdded = (symbol) => activeWL?.stocks?.some(s => s.symbol === symbol);

  const handleAddClick = (stock) => {
    if (isStockAdded(stock.symbol)) return;
    setShowStockForm(stock.symbol);
    setStockFormData({ symbol: stock.symbol, stockName: stock.stockName, entryPrice: '', targetPrice: '', stopLoss: '', notes: '' });
  };

  const confirmAddStock = async () => {
    setAddingStock(true);
    try {
      const payload = {
        symbol: stockFormData.symbol,
        stockName: stockFormData.stockName,
        notes: stockFormData.notes || '',
      };
      if (stockFormData.entryPrice) payload.entryPrice = stockFormData.entryPrice;
      if (stockFormData.targetPrice) payload.targetPrice = stockFormData.targetPrice;
      if (stockFormData.stopLoss) payload.stopLoss = stockFormData.stopLoss;

      const { data } = await api.post(`/watchlist/${activeWL.watchlistId}/add-stock`, payload);
      setActiveWL(data.data);
      setShowStockForm(null);
      setStockSearch('');
      setSearchResults([]);
      showToast(`${stockFormData.symbol} added to watchlist!`);
      fetchWatchlists();
    } catch (err) {
      showToast(err.response?.data?.message || 'Add failed', 'error');
    } finally {
      setAddingStock(false);
    }
  };

  const removeStock = async (symbol) => {
    if (!window.confirm(`Remove ${symbol} from watchlist?`)) return;
    try {
      const { data } = await api.delete(`/watchlist/${activeWL.watchlistId}/remove-stock/${symbol}`);
      setActiveWL(data.data);
      showToast(`${symbol} removed`);
      fetchWatchlists();
    } catch {
      showToast('Remove failed', 'error');
    }
  };

  /* ─── render ─────────────────────────────────────────────────────── */
  return (
    <AppLayout pageBadge="📋 Watchlist Manager">
      <div style={S.bg} />
      <Toast {...toast} />

      <div style={S.shell}>
        {/* Top bar */}
        <div style={S.topBar}>
          <div>
            <div style={S.pageTitle}>📋 Watchlist Manager</div>
            <div style={S.pageSub}>Create and manage your personal stock watchlists</div>
          </div>
          <button style={S.btnPrimary} onClick={openCreate}>+ Create Watchlist</button>
        </div>

        {/* Scrollable content */}
        <div style={S.content}>
          {loading ? (
            <div style={S.center}>
              <div style={S.spinner} />
              <div style={{ color: 'rgba(255,255,255,0.6)', marginTop: 12 }}>Loading watchlists...</div>
            </div>
          ) : watchlists.length === 0 ? (
            <div style={S.center}>
              <div style={{ fontSize: 64, opacity: 0.25, marginBottom: 16 }}>📋</div>
              <div style={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.7)', marginBottom: 8 }}>No watchlists yet</div>
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.88rem', marginBottom: 24, maxWidth: 340, textAlign: 'center' }}>
                Create your first watchlist to track your favourite stocks and monitor price levels.
              </div>
              <button style={S.btnPrimary} onClick={openCreate}>+ Create Your First Watchlist</button>
            </div>
          ) : (
            <div style={S.grid}>
              {watchlists.map(wl => (
                <div key={wl.watchlistId} style={S.card}>
                  {/* color strip */}
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: wl.color, borderRadius: '12px 12px 0 0' }} />
                  <div style={S.cardHead}>
                    <div style={{ flex: 1 }}>
                      <div style={S.cardName}>{wl.watchlistName}</div>
                      {wl.description && <div style={S.cardDesc}>{wl.description}</div>}
                    </div>
                    <div style={{ ...S.colorDot, background: wl.color }} />
                  </div>
                  <div style={S.cardStats}>
                    <div style={S.statBox}>
                      <div style={S.statVal}>{wl.stocks?.length || 0}</div>
                      <div style={S.statLbl}>Stocks</div>
                    </div>
                    <div style={S.statBox}>
                      <div style={{ ...S.statVal, fontSize: '1rem' }}>{wl.isActive ? '✓ Active' : '✗ Inactive'}</div>
                      <div style={S.statLbl}>Status</div>
                    </div>
                  </div>
                  <div style={S.cardActions}>
                    <button style={S.actionBtn} onClick={() => openManageStocks(wl)}>🔧 Manage Stocks</button>
                    <button style={S.actionBtn} onClick={(e) => openEdit(wl, e)}>✏️ Edit</button>
                    <button style={{ ...S.actionBtn, ...S.actionBtnDanger }} onClick={(e) => deleteWatchlist(wl, e)}>🗑️</button>
                  </div>
                  <div style={S.cardTs}>
                    Created {new Date(wl.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Create / Edit Modal ── */}
      {showCreateModal && (
        <div style={S.overlay} onClick={() => setShowCreateModal(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalHead}>
              <div style={S.modalTitle}>{editMode ? '✏️ Edit Watchlist' : '+ Create Watchlist'}</div>
              <button style={S.closeBtn} onClick={() => setShowCreateModal(false)}>✕</button>
            </div>

            <div style={S.formGroup}>
              <label style={S.label}>Watchlist Name *</label>
              <input
                style={S.input}
                placeholder="e.g. My Swing Trades"
                value={formData.watchlistName}
                onChange={e => setFormData(f => ({ ...f, watchlistName: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && saveWatchlist()}
              />
            </div>

            <div style={S.formGroup}>
              <label style={S.label}>Description (optional)</label>
              <textarea
                style={{ ...S.input, minHeight: 72, resize: 'vertical' }}
                placeholder="Brief description of this watchlist..."
                value={formData.description}
                onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
              />
            </div>

            <div style={S.formGroup}>
              <label style={S.label}>Color</label>
              <div style={S.colorGrid}>
                {COLORS.map(c => (
                  <div
                    key={c}
                    style={{
                      ...S.colorOpt,
                      background: c,
                      borderColor: formData.color === c ? '#fff' : 'transparent',
                      boxShadow: formData.color === c ? `0 0 10px ${c}88` : 'none',
                    }}
                    onClick={() => setFormData(f => ({ ...f, color: c }))}
                  />
                ))}
              </div>
            </div>

            <div style={S.modalFooter}>
              <button style={S.btnSecondary} onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button style={S.btnPrimary} onClick={saveWatchlist} disabled={saving}>
                {saving ? 'Saving...' : (editMode ? 'Update Watchlist' : 'Create Watchlist')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Manage Stocks Modal ── */}
      {showStocksModal && activeWL && (
        <div style={S.overlay} onClick={() => setShowStocksModal(false)}>
          <div style={{ ...S.modal, maxWidth: 780 }} onClick={e => e.stopPropagation()}>
            <div style={S.modalHead}>
              <div style={S.modalTitle}>
                🔧 Manage: {activeWL.watchlistName}
                <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', marginLeft: 8, fontWeight: 400 }}>
                  ({activeWL.stocks?.length || 0} stocks)
                </span>
              </div>
              <button style={S.closeBtn} onClick={() => setShowStocksModal(false)}>✕</button>
            </div>

            {/* Search section */}
            <div style={{ marginBottom: 20 }}>
              <label style={S.label}>Search & Add Stocks</label>
              <input
                style={S.input}
                placeholder="🔍  Type symbol or company name..."
                value={stockSearch}
                onChange={e => setStockSearch(e.target.value)}
              />
              {searchLoading && (
                <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', marginTop: 5 }}>Searching...</div>
              )}

              {searchResults.length > 0 && (
                <div style={S.searchList}>
                  {searchResults.map(stock => {
                    const added = isStockAdded(stock.symbol);
                    const isFormOpen = showStockForm === stock.symbol;
                    return (
                      <div key={stock.symbol} style={S.searchItem}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#fff' }}>{stock.symbol}</div>
                          <div style={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.5)' }}>{stock.stockName}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                          {stock.categories?.map(c => (
                            <span key={c} style={c === 'N50' ? S.badgeN50 : c === 'FO' ? S.badgeFO : S.badgeOthers}>{c}</span>
                          ))}
                          {!isFormOpen && (
                            <button
                              style={added ? S.addedBtn : S.addBtn}
                              disabled={added}
                              onClick={() => handleAddClick(stock)}
                            >
                              {added ? '✓ Added' : '+ Add'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Inline stock add form */}
            {showStockForm && (
              <div style={S.stockForm}>
                <div style={{ fontWeight: 600, marginBottom: 12, color: '#fff', fontSize: '0.92rem' }}>
                  Adding: <span style={{ color: '#667eea' }}>{stockFormData.symbol}</span>
                  <span style={{ fontWeight: 400, fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', marginLeft: 6 }}>
                    — all fields below are optional
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                  {[
                    { field: 'entryPrice', label: 'Entry Price ₹' },
                    { field: 'targetPrice', label: 'Target Price ₹' },
                    { field: 'stopLoss', label: 'Stop Loss ₹' },
                  ].map(({ field, label }) => (
                    <div key={field}>
                      <label style={{ ...S.label, fontSize: '0.75rem' }}>{label}</label>
                      <input
                        style={S.input}
                        type="number"
                        placeholder="0.00"
                        value={stockFormData[field]}
                        onChange={e => setStockFormData(f => ({ ...f, [field]: e.target.value }))}
                      />
                    </div>
                  ))}
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ ...S.label, fontSize: '0.75rem' }}>Notes</label>
                  <input
                    style={S.input}
                    placeholder="Optional notes about this stock..."
                    value={stockFormData.notes}
                    onChange={e => setStockFormData(f => ({ ...f, notes: e.target.value }))}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={S.btnSecondary} onClick={() => setShowStockForm(null)}>Cancel</button>
                  <button style={S.btnPrimary} onClick={confirmAddStock} disabled={addingSock}>
                    {addingSock ? 'Adding...' : `Add ${stockFormData.symbol}`}
                  </button>
                </div>
              </div>
            )}

            {/* Existing stocks list */}
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.88rem', color: 'rgba(255,255,255,0.7)', marginBottom: 10 }}>
                Stocks in this Watchlist
              </div>
              {(!activeWL.stocks || activeWL.stocks.length === 0) ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'rgba(255,255,255,0.35)', fontSize: '0.85rem' }}>
                  No stocks added yet. Use the search above to add stocks.
                </div>
              ) : (
                <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                  {activeWL.stocks.map(stock => (
                    <div key={stock.symbol} style={S.stockItem}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#fff' }}>{stock.symbol}</div>
                        <div style={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.5)' }}>{stock.stockName}</div>
                        {(stock.entryPrice || stock.targetPrice || stock.stopLoss) && (
                          <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.38)', marginTop: 2 }}>
                            {stock.entryPrice && `Entry: ₹${parseFloat(stock.entryPrice).toFixed(2)}`}
                            {stock.targetPrice && ` · Target: ₹${parseFloat(stock.targetPrice).toFixed(2)}`}
                            {stock.stopLoss && ` · SL: ₹${parseFloat(stock.stopLoss).toFixed(2)}`}
                          </div>
                        )}
                      </div>
                      <button style={S.removeBtn} onClick={() => removeStock(stock.symbol)}>✕ Remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

/* ─── Styles ─────────────────────────────────────────────────────── */
const S = {
  bg: {
    position: 'fixed', inset: 0, zIndex: 0,
    background: 'linear-gradient(135deg, #0d0d1a 0%, #111827 50%, #0a1628 100%)',
    pointerEvents: 'none',
  },
  shell: {
    position: 'relative', zIndex: 1,
    height: `calc(100vh - ${HEADER_H}px)`,
    marginTop: HEADER_H,
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  topBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 24px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    flexShrink: 0,
  },
  pageTitle: {
    fontSize: '1.3rem', fontWeight: 700,
    background: 'linear-gradient(135deg,#667eea,#764ba2)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  pageSub: { fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', marginTop: 2 },
  content: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 24px' },
  center: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', height: '100%', minHeight: 300,
    textAlign: 'center', padding: 24,
  },
  spinner: {
    width: 40, height: 40,
    border: '3px solid rgba(255,255,255,0.08)',
    borderTop: '3px solid #667eea',
    borderRadius: '50%', animation: 'spin 1s linear infinite',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 20,
  },
  card: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: 12, padding: '1.25rem',
    position: 'relative', overflow: 'hidden',
    transition: 'border-color 0.2s',
  },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, marginTop: 6 },
  cardName: { fontSize: '1.05rem', fontWeight: 700, color: '#fff', marginBottom: 3 },
  cardDesc: { fontSize: '0.74rem', color: 'rgba(255,255,255,0.5)' },
  colorDot: { width: 28, height: 28, borderRadius: 6, flexShrink: 0, boxShadow: '0 4px 10px rgba(0,0,0,0.3)' },
  cardStats: { display: 'flex', gap: 10, marginBottom: 14 },
  statBox: {
    flex: 1, background: 'rgba(255,255,255,0.03)',
    padding: '8px', borderRadius: 8, textAlign: 'center',
  },
  statVal: {
    fontSize: '1.35rem', fontWeight: 700,
    background: 'linear-gradient(135deg,#00f260,#0575e6)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  statLbl: { fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', marginTop: 2 },
  cardActions: { display: 'flex', gap: 6, marginBottom: 10 },
  actionBtn: {
    flex: 1, padding: '6px 8px',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: 6, color: '#fff', fontSize: '0.78rem', cursor: 'pointer',
  },
  actionBtnDanger: {
    color: '#ff6b6b', flex: 'none', minWidth: 36,
    borderColor: 'rgba(255,107,107,0.2)', background: 'rgba(255,107,107,0.06)',
  },
  cardTs: { fontSize: '0.67rem', color: 'rgba(255,255,255,0.35)', textAlign: 'center' },
  btnPrimary: {
    background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff',
    padding: '8px 18px', borderRadius: 8, border: 'none', fontWeight: 600,
    cursor: 'pointer', fontSize: '0.88rem', whiteSpace: 'nowrap',
    boxShadow: '0 4px 15px rgba(102,126,234,0.3)',
  },
  btnSecondary: {
    background: 'rgba(255,255,255,0.05)', color: '#fff',
    padding: '8px 18px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
    fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem',
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)',
    backdropFilter: 'blur(6px)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
  },
  modal: {
    background: 'rgba(18,18,38,0.98)', backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16,
    padding: '1.5rem', maxWidth: 520, width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.55)', maxHeight: '90vh', overflowY: 'auto',
  },
  modalHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  modalTitle: {
    fontSize: '1.1rem', fontWeight: 700,
    background: 'linear-gradient(135deg,#667eea,#764ba2)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  closeBtn: {
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    color: '#fff', width: 28, height: 28, borderRadius: '50%',
    cursor: 'pointer', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  formGroup: { marginBottom: 16 },
  label: { display: 'block', marginBottom: 6, fontSize: '0.85rem', fontWeight: 500, color: 'rgba(255,255,255,0.85)' },
  input: {
    width: '100%', padding: '8px 12px',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8, color: '#fff', fontSize: '0.88rem',
    outline: 'none', boxSizing: 'border-box',
  },
  colorGrid: { display: 'grid', gridTemplateColumns: 'repeat(8,1fr)', gap: 6, marginTop: 4 },
  colorOpt: {
    height: 26, borderRadius: 4, border: '2px solid transparent',
    cursor: 'pointer', transition: 'transform 0.15s',
  },
  modalFooter: { display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' },
  searchList: {
    maxHeight: 260, overflowY: 'auto', marginTop: 8,
    background: 'rgba(255,255,255,0.02)', borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.07)',
  },
  searchItem: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '9px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)',
  },
  badgeN50: {
    padding: '2px 7px', borderRadius: 4, fontSize: '0.63rem', fontWeight: 700,
    background: 'linear-gradient(135deg,#00f260,#0575e6)', color: '#fff',
  },
  badgeFO: {
    padding: '2px 7px', borderRadius: 4, fontSize: '0.63rem', fontWeight: 700,
    background: 'linear-gradient(135deg,#4facfe,#00f2fe)', color: '#fff',
  },
  badgeOthers: {
    padding: '2px 7px', borderRadius: 4, fontSize: '0.63rem', fontWeight: 700,
    background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.85)',
    border: '1px solid rgba(255,255,255,0.18)',
  },
  addBtn: {
    background: 'linear-gradient(135deg,#00f260,#0575e6)', color: '#fff',
    padding: '4px 12px', borderRadius: 6, border: 'none',
    fontWeight: 700, cursor: 'pointer', fontSize: '0.78rem', whiteSpace: 'nowrap',
  },
  addedBtn: {
    background: 'rgba(0,242,96,0.08)', color: '#00f260',
    padding: '4px 12px', borderRadius: 6, border: '1px solid rgba(0,242,96,0.25)',
    fontWeight: 700, cursor: 'not-allowed', fontSize: '0.78rem', whiteSpace: 'nowrap',
  },
  stockForm: {
    background: 'rgba(102,126,234,0.07)', border: '1px solid rgba(102,126,234,0.18)',
    borderRadius: 10, padding: '14px', marginBottom: 16,
  },
  stockItem: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 12px', background: 'rgba(255,255,255,0.03)',
    borderRadius: 8, marginBottom: 6, border: '1px solid rgba(255,255,255,0.05)',
  },
  removeBtn: {
    background: 'rgba(255,107,107,0.08)', color: '#ff6b6b',
    padding: '4px 10px', borderRadius: 4, border: '1px solid rgba(255,107,107,0.2)',
    cursor: 'pointer', fontSize: '0.75rem', whiteSpace: 'nowrap', flexShrink: 0,
  },
};
